import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Check, CreditCard, Loader2, ShieldCheck } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { money } from '@/lib/money'
import { tierIcon, tierTint } from '@/lib/tiers'
import type { WalletInfo } from '@/types'
import Card from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import { Field, Input } from '@/components/ui/Field'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'
import { cn } from '@/lib/cn'

const QUICK_AMOUNTS = ['100', '500', '1000', '2000', '5000']
const MIN_TOPUP = 10

export default function TopUp() {
  const toast = useToast()
  const navigate = useNavigate()
  const [amount, setAmount] = useState('500')
  const [selectedTierId, setSelectedTierId] = useState<number | null>(null)
  const [paying, setPaying] = useState(false)

  const wallet = useQuery({
    queryKey: ['wallet'],
    queryFn: async () => (await api.get<WalletInfo>('/wallet')).data,
  })

  const topup = useMutation({
    mutationFn: async () => (await api.post('/wallet/topup', { amount: Number(amount) })).data,
    onSuccess: (data: { checkout_url: string; topup_id: number }) => {
      // The gateway decides the return URL, so it cannot carry our id. Stash
      // it here for the return page to poll.
      sessionStorage.setItem('mpg_pending_topup', String(data.topup_id))
      // Hand the browser to the gateway's own payment page. The wallet is not
      // credited here or on the way back: only the gateway's signed webhook
      // does that, so nothing about this navigation can move money.
      window.location.href = data.checkout_url
    },
    onError: (err) => {
      toast.error('Could not start the payment', errorMessage(err))
      setPaying(false)
    },
  })

  const startPayment = () => {
    setPaying(true)
    topup.mutate()
  }

  if (wallet.isLoading) return <LogoLoader />

  const info = wallet.data
  const lifetime = info?.lifetime_topup ?? 0
  // A tier set by hand can sit above what lifetime top-up earned; every tier up
  // to it counts as unlocked. A locked tier can also sit below it, and then the
  // tiers above it are not theirs whatever they have topped up - nor can any
  // card above it offer itself as a target, since top-ups do not move it.
  const tierLocked = Boolean(info?.tier_locked)
  const currentThreshold = info?.tier?.topup_threshold ?? 0
  const standing = tierLocked ? currentThreshold : Math.max(lifetime, currentThreshold)
  const payAmount = Number(amount) || 0

  const pickTier = (tierId: number, threshold: number) => {
    setAmount(String(Math.max(MIN_TOPUP, threshold - lifetime)))
    setSelectedTierId(tierId)
  }

  return (
    <div>
      <PageHeader
        title="Top up wallet"
        subtitle={
          <>
            Current balance <span className="font-bold text-ink">{money(info?.balance)}</span> - lifetime top-up{' '}
            {money(lifetime)}
          </>
        }
        actions={
          <Button variant="outline" onClick={() => navigate('/account/wallet')}>
            <ArrowLeft className="size-4" /> Back to wallet
          </Button>
        }
      />

      <Card>
        <Field label="Top up amount (RM)">
          <Input
            type="number"
            min={MIN_TOPUP}
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value)
              setSelectedTierId(null)
            }}
          />
        </Field>
        <p className="mb-2 mt-4 text-2xs font-bold uppercase tracking-wide text-muted">Quick amount</p>
        <div className="flex flex-wrap gap-2">
          {QUICK_AMOUNTS.map((preset) => (
            <button
              key={preset}
              onClick={() => {
                setAmount(preset)
                setSelectedTierId(null)
              }}
              className={cn(
                'rounded-lg border px-4 py-2 text-sm font-bold',
                amount === preset ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-line hover:border-brand-300',
              )}
            >
              RM {preset}
            </button>
          ))}
        </div>
      </Card>

      <h2 className="mb-3 mt-8 text-sm font-bold uppercase tracking-wide text-muted">Membership tiers</h2>
      <div className="grid gap-3 sm:grid-cols-3">
        {info?.tiers.map((t) => {
          const Icon = tierIcon(t.icon)
          const tint = tierTint(t.color)
          const isCurrent = info.tier?.id === t.id
          const unlocked = t.topup_threshold <= standing
          const pickable = !unlocked && !tierLocked
          const isSelected = selectedTierId === t.id
          const delta = Math.max(MIN_TOPUP, t.topup_threshold - lifetime)
          const body = (
            <>
              <div
                className="relative flex items-center justify-center rounded-t-xl border-b px-3 py-2 text-sm font-bold uppercase"
                style={{ backgroundColor: tint.backgroundColor, borderColor: tint.borderColor, color: tint.color }}
              >
                {t.name}
                {/* The check marks the tier the customer just picked as their
                    top-up target - never the tier they already hold, which
                    reads as its own tinted "you are here" card below. */}
                {isSelected && (
                  <span
                    className="absolute right-2 flex size-5 items-center justify-center rounded-full text-white"
                    style={{ backgroundColor: tint.color }}
                  >
                    <Check className="size-3.5" />
                  </span>
                )}
              </div>
              <div className="p-4 text-center">
                <span className="mx-auto flex size-9 items-center justify-center rounded-full border" style={tint}>
                  <Icon className="size-4" />
                </span>
                <p className="mt-2 text-2xs uppercase text-foreground/50">Min. lifetime top-up</p>
                <p className="text-xl font-extrabold text-primary">{money(t.topup_threshold)}</p>
                <p className="mt-1 text-2xs uppercase text-foreground/50">Flat member prices on all products</p>
                {isCurrent ? (
                  <p className="mt-3 text-xs font-bold uppercase" style={{ color: tint.color }}>
                    Your tier
                  </p>
                ) : unlocked ? (
                  <p className="mt-3 inline-flex items-center justify-center gap-1 text-xs font-bold uppercase text-success">
                    <Check className="size-3.5" /> Unlocked
                  </p>
                ) : tierLocked ? (
                  <p className="mt-3 text-xs font-semibold text-foreground/50">Not available by top-up for now</p>
                ) : isSelected ? (
                  <p className="mt-3 text-xs font-bold uppercase" style={{ color: tint.color }}>
                    Selected - pay {money(delta)} to unlock
                  </p>
                ) : (
                  <p className="mt-3 text-xs font-semibold" style={{ color: tint.color }}>
                    Top up {money(delta)} more to unlock
                  </p>
                )}
              </div>
            </>
          )
          // Two distinct treatments so only one card ever looks "chosen": the
          // clicked target gets the ring + check, the tier already held gets a
          // passive tinted fill (the same recipe as the product page's tier
          // cards). Before this both wore the identical ring and it read as
          // two selections at once.
          const frame = isSelected
            ? { borderColor: tint.color, boxShadow: `0 0 0 1px ${tint.color}` }
            : isCurrent
              ? { borderColor: tint.color, backgroundColor: tint.backgroundColor }
              : { borderColor: tint.borderColor }
          return !pickable ? (
            <div key={t.id} className={cn('rounded-xl border bg-background', !isCurrent && 'opacity-70')} style={frame}>
              {body}
            </div>
          ) : (
            <button
              key={t.id}
              onClick={() => pickTier(t.id, t.topup_threshold)}
              className="rounded-xl border bg-background text-left transition-shadow hover:shadow-md"
              style={frame}
            >
              {body}
            </button>
          )
        })}
      </div>
      <p className="mt-2 text-xs text-muted">
        {tierLocked
          ? 'Your tier was set by MPG, so top-ups do not change it for now. Your current tier is highlighted.'
          : 'Tiers are based on lifetime top-up. Your current tier is highlighted - click a locked tier to set the exact amount needed to unlock it.'}
      </p>

      <div className="mt-6 space-y-3">
        <Button className="w-full" disabled={payAmount < MIN_TOPUP} onClick={startPayment}>
          Pay {money(payAmount)}
        </Button>
        <p className="flex items-center gap-2 rounded-lg bg-app p-2.5 text-xs text-muted">
          <ShieldCheck className="size-4 text-status-success" />
          You will be taken to the payment gateway to complete this securely. Your wallet is credited once the
          payment is confirmed.
        </p>
        <p className="flex items-center gap-2 text-xs text-muted">
          <CreditCard className="size-4" /> FPX online banking, cards and e-wallets.
        </p>
      </div>

      <Modal open={paying} onClose={() => {}} title="Top up wallet">
        <div className="py-6 text-center">
          <Loader2 className="mx-auto size-8 animate-spin text-brand-500" />
          <p className="mt-3 text-sm font-semibold">Taking you to the payment page...</p>
          <p className="mt-1 text-xs text-muted">Do not close this tab.</p>
        </div>
      </Modal>
    </div>
  )
}
